import { Component, EventEmitter, Input, OnInit, Output, Self, ViewChild, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { ToastrService } from 'ngx-toastr';
import { EquipmentManagenementService } from 'src/app/services/equipmentmanagement.service';
import { SaveService } from 'src/app/services/save.service';
import { DefaultValuesService } from '../../../../../../services/defaultvalues.service';
import { forkJoin, of, takeUntil } from 'rxjs';
import { DestroyService } from 'src/app/services/destroyservice';
import { SoftwareReleaseItem } from '../../types/software-release';
import { EmtAllowedSoftwareRelease, EmtAllowedSoftwareReleaseLevelDto, EmtAllowedSoftwareReleaseType, EmtAllowedSoftwareReleaseValue } from 'src/app/interfaces/emt/emt-allowed-softwarer-release';
import { MatSort } from '@angular/material/sort';

@Component({
    selector: 'software-release-list',
    templateUrl: './software-release-list.component.html',
    styleUrls: ['./software-release-list.component.less', '../../../../../../style/new-generic-styles.component.less'],
    encapsulation: ViewEncapsulation.None,
    providers: [DestroyService],
    standalone: false
})
export class SoftwareReleaseListComponent implements OnInit {
    public readonly ENVIRONMENT = "environment";
    public readonly TENANT = "tenant";
    public readonly COLUMNS = {
        [this.ENVIRONMENT]: ['board', 'familyCode', 'version', 'environment', 'actions'],
        [this.TENANT]: ['board', 'familyCode', 'version', 'tenant', 'environment', 'actions'],
    };
    public readonly environmentValues = [
        { text: 'Allowed', value: EmtAllowedSoftwareReleaseValue.Allowed },
        { text: 'Not Allowed', value: EmtAllowedSoftwareReleaseValue.NotAllowed },
    ];
    public readonly tenantValues = [
        { text: 'Inherit from environment', value: EmtAllowedSoftwareReleaseValue.NotSet },
        ...this.environmentValues
    ];

    public form: FormGroup;
    public columnsToDisplay = this.COLUMNS[this.ENVIRONMENT];
    public editableColumn = this.ENVIRONMENT;

    @Input()
    public isPeripheral: boolean = false;

    @Input()
    public softwareReleases: SoftwareReleaseItem[] = []

    @Input()
    public familyCodes: Map<number, { familyCode: string; familyCodeName: string }> = new Map();

    public selectedLevel?: EmtAllowedSoftwareReleaseLevelDto;
    @ViewChild(MatSort) sort!: MatSort;
    @Output()
    public saveSuccess = new EventEmitter<void>();

    public dataSource = new MatTableDataSource<any>();

    constructor(
        private readonly emtService: EquipmentManagenementService,
        private readonly toastrService: ToastrService,
        private readonly saveService: SaveService,
        private readonly formBuilder: FormBuilder,
        public readonly defaults: DefaultValuesService,
        private cdr: ChangeDetectorRef,
        @Self() private readonly destroy$: DestroyService
    ) {
        this.form = this.formBuilder.group({
            rows: this.formBuilder.array([])
        });
    }

    public ngOnInit() {
        this.defaults.stageMessageChanges$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.selectedLevel = undefined;
                this.clearForm();
            });

        this.clearForm();
        this.updateHeaders();

        if (this.selectedLevel) {
            this.loadSoftwareReleases();
        }
    }

    public changeSelectedLevel(newLevel: EmtAllowedSoftwareReleaseLevelDto) {
        this.selectedLevel = newLevel;
        this.clearForm();
        this.updateHeaders();
        this.loadSoftwareReleases();
    }

    public onRowEditInit(row: any) {
        const formIndex = this.findFormArrayIndex(row);
        if (formIndex === -1) return;
        const rows = this.form.get('rows') as FormArray;
        rows.at(formIndex).get('onEdit')?.patchValue(true);
        this.updateDataSource((this.form.get('rows') as FormArray).value);
    }

    public onRowEditCancel(row: any) {
        const formIndex = this.findFormArrayIndex(row);
        if (formIndex === -1) return;
        this.updateFormValue(formIndex, 'onEdit', false);
        this.resetFormValue(formIndex, this.editableColumn === this.ENVIRONMENT ? 'environmentValue' : 'tenantValue');
        this.updateDataSource((this.form.get('rows') as FormArray).value);
    }

    public onSave() {
        const rows = this.form.get('rows') as FormArray;
        const modifiedRows = this.form.value.rows
            .map((row: any, i: number) => ({ ...row, index: i }))
            .filter((x: any) => x.onEdit);

        if (!modifiedRows.length) {
            return;
        }

        const request = {
            ...this.selectedLevel!,
            softwaresReleases: modifiedRows.map(x => ({
                id: x.id,
                type: x.type,
                value: this.editableColumn === this.ENVIRONMENT
                    ? x.environmentValue
                    : x.tenantValue,
            })),
        };

        this.emtService
            .updateAllowedSoftwareReleases(request)
            .subscribe({
                next: () => {
                    modifiedRows.forEach(row => {
                        const control = rows.at(row.index);
                        control.get('onEdit')?.setValue(false);
                        control.get('unmodified')?.setValue({
                            environmentValue: row.environmentValue,
                            tenantValue: row.tenantValue
                        });
                    });
                    this.updateDataSource(rows.value);
                    this.toastrService.success('Software releases saved.');
                    this.saveSuccess.emit(); 
                },
                error: (error) => {
                    this.toastrService.error(error.error);
                },
            });
    }

    public hasChanges() {
        return this.form.value.rows.some(x => x.onEdit);
    }

    public formatEnvironmentValue(value: EmtAllowedSoftwareReleaseValue) : string {
        switch (value) {
            case EmtAllowedSoftwareReleaseValue.NotSet:
            case EmtAllowedSoftwareReleaseValue.NotAllowed:
                return 'Not allow';
            case EmtAllowedSoftwareReleaseValue.Allowed:
                return 'Allow';
        }
    }

    public formatTenantValue(tenantValue: EmtAllowedSoftwareReleaseValue, environmentValue: EmtAllowedSoftwareReleaseValue) : string {
        switch (tenantValue) {
            case EmtAllowedSoftwareReleaseValue.NotSet:
                return this.formatEnvironmentValue(environmentValue) + ' - Inherited';
            case EmtAllowedSoftwareReleaseValue.Allowed:
                return 'Allow';
            case EmtAllowedSoftwareReleaseValue.NotAllowed:
                return 'Not allow';
        }
    }

    public formatType(type: EmtAllowedSoftwareReleaseType) {
        return type === EmtAllowedSoftwareReleaseType.Controller ? 'Controller' : 'Peripheral';
    }

    private clearForm() {
        this.form = this.formBuilder.group({
            rows: this.formBuilder.array([]),
        });
        this.form.reset();
        this.dataSource.data = [];
    }

    private updateHeaders() {
        if (this.selectedLevel && this.selectedLevel.tenantId) {
            this.columnsToDisplay = this.COLUMNS[this.TENANT];
            this.editableColumn = this.TENANT;
        } else {
            this.columnsToDisplay = this.COLUMNS[this.ENVIRONMENT];
            this.editableColumn = this.ENVIRONMENT;
        }
    }

    private loadSoftwareReleases() {
        const isTenant = !!this.selectedLevel?.tenantId;
        const getEnvironmentReleases$ = this.emtService.getAllowedSoftwareReleases({ ...this.selectedLevel!, tenantId: undefined })
        const getTenantReleases$ = isTenant
            ? this.emtService.getAllowedSoftwareReleases(this.selectedLevel!)
            : of(new Array<EmtAllowedSoftwareRelease>());

        forkJoin([getEnvironmentReleases$, getTenantReleases$])
            .subscribe({
                next: ([environmentReleases, tenantReleases]) => {
                    this.fillForm(environmentReleases, tenantReleases);
                },
                error: () => {
                    this.toastrService.error("Unable to load software releases!");
                }
            });
    }

    public getFormControlByElement(element: any, field: string): FormControl<any> {
        const rows = this.form.get('rows') as FormArray;
        const idx = this.findFormArrayIndex(element);
        if (idx === -1) throw new Error('Element not found in FormArray');
        const control = rows.at(idx).get(field);
        if (!control) throw new Error(`FormControl '${field}' not found`);
        return control as FormControl<any>;
    }

    private fillForm(environmentReleases: EmtAllowedSoftwareRelease[], tenantReleases: EmtAllowedSoftwareRelease[]) {
        const releaseKey = (r: { type: EmtAllowedSoftwareReleaseType, id: number }) => `${r.type}-${r.id}`;
        const environmentReleasesMap = new Map(environmentReleases.map(r => [releaseKey(r), r]));
        const tenantReleasesMap = new Map(tenantReleases.map(r => [releaseKey(r), r]));

        this.form = this.formBuilder.group({
            rows: this.formBuilder.array(
                this.softwareReleases.map(release => {
                    const key = releaseKey(release);
                    const environmentValue = environmentReleasesMap.get(key)?.value ?? EmtAllowedSoftwareReleaseValue.NotAllowed;
                    const tenantValue = tenantReleasesMap.get(key)?.value ?? EmtAllowedSoftwareReleaseValue.NotSet;

                    return this.formBuilder.group({
                        id: new FormControl(release.id),
                        type: new FormControl(release.type),
                        version: new FormControl(release.version),
                        releaseName: new FormControl(release.releaseName),
                        familyCode: new FormControl(this.familyCodes.get(release.familyCodeId)?.familyCode),
                        board: new FormControl(this.familyCodes.get(release.familyCodeId)?.familyCodeName),
                        environmentValue: new FormControl(environmentValue),
                        tenantValue: new FormControl(tenantValue),
                        onEdit: new FormControl(false),
                        unmodified: new FormControl({ environmentValue, tenantValue })
                    });
                }))
        });

        this.updateDataSource((this.form.get('rows') as FormArray).value);
    }

    private updateDataSource(rows: any[]) {
        this.dataSource = new MatTableDataSource(rows);
        this.dataSource.sortData = (data, sort) => {
            if (!sort.active || sort.direction === '') {
                return data;
            }
            const isAsc = sort.direction === 'asc';
            return data.slice().sort((a, b) => {
                switch (sort.active) {
                    case 'version':
                        return (a.version || '').localeCompare(b.version || '') * (isAsc ? 1 : -1);
                    case 'type':
                        return (this.formatType(a.type) || '').localeCompare(this.formatType(b.type) || '') * (isAsc ? 1 : -1);
                    case 'familyCode':
                        return (a.familyCode || '').localeCompare(b.familyCode || '') * (isAsc ? 1 : -1);
                    case 'board':
                        return (a.board || '').localeCompare(b.board || '') * (isAsc ? 1 : -1);
                    case 'environment':
                        return (this.formatEnvironmentValue(a.environmentValue) || '')
                            .localeCompare(this.formatEnvironmentValue(b.environmentValue) || '') * (isAsc ? 1 : -1);
                    case 'tenant':
                        return (this.formatTenantValue(a.tenantValue, a.environmentValue) || '')
                            .localeCompare(this.formatTenantValue(b.tenantValue, b.environmentValue) || '') * (isAsc ? 1 : -1);
                    default:
                        return 0;
                }
            });
        };
        this.dataSource.sort = this.sort;
        this.cdr.detectChanges();
    }

    private updateFormValue(index: number, fieldName: string, newValue: any) {
        const rows = this.form.get('rows') as FormArray;
        const row = rows.at(index);
        row.get(fieldName)?.setValue(newValue);
    }

    private resetFormValue(index: number, fieldName: string) {
        const rows = this.form.get('rows') as FormArray;
        const row = rows.at(index);
        const value = row.value.unmodified[fieldName];
        row.get(fieldName)?.setValue(value);
    }

    private findFormArrayIndex(row: any): number {
        const rows = this.form.get('rows') as FormArray;
        return rows.controls.findIndex(control =>
            control.value.id === row.id && control.value.type === row.type
        );
    }
}
